// Area homes: each area's home is that department's own page. It opens on the area's name and Courage Chat, then the
// area's analytics (its own dashboard, which the people in the area edit like any dashboard), then its apps as tiles
// that say what's open in each. Drawn in the app's own Business Central style: no per-area colors or badges.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type AreaHome, type Dashboard, type DSpec, type XPeriod, type XQuery } from "./api";
import { appsOf } from "./pages/Home";
import { APP_SPECS, type AppSpec } from "./apps/specs";
import { BarsTile, ChartTile, HeatmapTile, KpiTile, PERIODS, periodId, periodText, planTile, TableTile, TextTile, type Plan, type TileRun } from "./analytics/tiles";
import type { Domain } from "./suite";
import { can, useAccess } from "./security/store";

/** The area's page title and what it covers, with Courage Chat under it on the area home. */
export function SiteHeader({ d, summary, children }: { d: Domain; summary?: string; children?: ReactNode }) {
  return (
    <header className="home-head">
      <h1>{d.name}</h1>
      <p>{summary ?? d.summary}</p>
      {children && <div className="home-head-chat">{children}</div>}
    </header>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------------------------------------------

const ROW = 118, GAP = 12, HEAD = 42;

/** The area's dashboard, drawn on its home: the tiles as saved, with a period the viewer can change for themselves. */
export function AreaAnalytics({ d }: { d: Domain }) {
  const nav = useNavigate();
  const access = useAccess();
  const [dash, setDash] = useState<Dashboard | null | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);
  const key = `area.period.${d.id}`;
  const [own, setOwn] = useState<XPeriod | null | undefined>(() => { try { const v = localStorage.getItem(key); return v === null ? undefined : (JSON.parse(v) as XPeriod | null); } catch { return undefined; } });
  const [runs, setRuns] = useState<Record<string, TileRun>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    setDash(undefined); setRuns({}); setErr(null);
    api.areaDashboard(d.id).then((r) => { if (live) setDash(r.dashboard); }, (e) => { if (live) { setDash(null); setErr(e instanceof Error ? e.message : String(e)); } });
    return () => { live = false; };
  }, [d.id]);

  const spec: DSpec | null = dash ? { ...dash.spec, period: own === undefined ? dash.spec.period : own, slicers: [] } : null;
  const plans = useMemo(() => (spec ? spec.tiles.map((t) => planTile(t, spec, {})).filter((p): p is Plan => !!p) : []), [JSON.stringify(spec)]); // eslint-disable-line react-hooks/exhaustive-deps
  const planKey = JSON.stringify(plans.map((p) => [p.tile.id, p.main, p.compare, p.spark]));
  useEffect(() => {
    if (!plans.length) { setRuns({}); return; }
    let live = true;
    setLoading(true);
    (async () => {
      const qs: XQuery[] = [], idx: { id: string; part: "main" | "compare" | "spark" }[] = [];
      for (const p of plans) for (const part of ["main", "compare", "spark"] as const) { const q = p[part]; if (q) { qs.push(q); idx.push({ id: p.tile.id, part }); } }
      try {
        const { results } = await api.xBatch(qs);
        if (!live) return;
        const next: Record<string, TileRun> = {};
        for (const p of plans) next[p.tile.id] = { compare: p.compare ? null : undefined, spark: null };
        results.forEach((r, i) => { const { id, part } = idx[i]; if (r.ok) (next[id] as any)[part] = r.res; else if (part === "main") next[id].error = r.error; });
        setRuns(next);
      } catch (e) { if (live) setErr(e instanceof Error ? e.message : String(e)); } finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [planKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (id: string) => {
    const p = PERIODS.find((x) => x.id === id)!;
    const next = p.period ? { ...p.period, anchor: dash?.spec.period?.anchor ?? "today" } : null;
    setOwn(next);
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* private window */ }
  };
  const add = async () => {
    try { const n = await api.addAreaDashboard(d.id, { name: `${d.name} analytics` }); nav(`/dashboards/${n.id}?edit=1`); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  // The tiles run on the analytics engine: people who can't see dashboards and workbooks don't get the section.
  if (access && !(can(access, "dashboards", "read") && can(access, "workbooks", "read"))) return null;
  if (dash === undefined) return <section className="site-ana" aria-label={`${d.name} analytics`}><div className="dboard-grid site-grid"><div className="dtile k-kpi" style={{ gridColumn: "span 12", gridRow: "span 1" }} /></div></section>;
  if (!dash) {
    if (!access?.realAdmin && !can(access, "dashboards", "edit")) return null;
    return (
      <section className="site-ana-none">
        <span>No analytics on this home yet. Add KPIs and charts from any dataset; everyone in {d.name} sees them here.</span>
        <button className="btn" onClick={add}>Add analytics</button>
        {err && <span className="bad">{err}</span>}
      </section>
    );
  }
  const range = periodText(runs);
  return (
    <section className="site-ana" aria-label={`${d.name} analytics`}>
      <div className="site-ana-bar">
        {spec!.period !== undefined && spec!.tiles.some((t) => t.date) && (
          <select className="select" aria-label="Period" value={periodId(spec!.period)} onChange={(e) => choose(e.target.value)}>
            {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        )}
        <span className="site-ana-range">
          {loading && <span className="spin" aria-label="Updating" />}
          {!spec!.tiles.some((t) => t.date) && <span className="muted">As of now</span>}
          {range && <strong>{range}</strong>}
        </span>
        <span className="sp" />
        <Link to={`/dashboards/${dash.id}`}>Open as a dashboard</Link>
        {dash.canEdit && can(access, "dashboards", "edit") && <Link to={`/dashboards/${dash.id}?edit=1`}>Edit analytics</Link>}
      </div>
      {err && <div className="notify warn" role="alert"><span>{err}</span></div>}
      {!dash.spec.tiles.length ? (
        <div className="site-ana-none"><span>This home's analytics have no tiles yet.</span>{dash.canEdit && <Link className="btn" to={`/dashboards/${dash.id}?edit=1`}>Add tiles</Link>}</div>
      ) : (
        <div className={`dboard-grid site-grid ${loading ? "busy" : ""}`}>
          {dash.spec.tiles.map((t) => {
            const run = runs[t.id] ?? {};
            const bodyH = t.h * ROW + (t.h - 1) * GAP - HEAD - 12;
            return (
              <section key={t.id} className={`dtile k-${t.kind}`} style={{ gridColumn: `span ${t.w}`, gridRow: `span ${t.h}` }} aria-label={t.title}>
                <header className="dtile-h"><div className="dtile-t"><h2>{t.title}</h2>{t.note && t.kind !== "kpi" && <span>{t.note}</span>}</div></header>
                <div className="dtile-b">
                  {run.error ? <div className="dtile-err">{run.error}</div>
                    : t.kind === "kpi" ? <KpiTile tile={t} run={run} spec={spec!} />
                    : t.kind === "chart" ? <ChartTile tile={t} run={run} height={bodyH} />
                    : t.kind === "bars" ? <BarsTile tile={t} run={run} />
                    : t.kind === "table" ? <TableTile tile={t} run={run} />
                    : t.kind === "heatmap" ? <HeatmapTile tile={t} run={run} />
                    : <TextTile tile={t} />}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Apps
// ---------------------------------------------------------------------------------------------------------------

const ENDS = new Set(["passed", "rejected", "expired", "retired", "not_now", "obsolete", "left", "cancelled"]);
/** The stages where a record in an app on the shared record engine is finished: its last stage, any stage marked
 *  done (ok), and stages that end it another way (rejected, expired, retired…). */
export function doneStages(s: AppSpec): string[] {
  return s.stages.filter((x, i) => i === s.stages.length - 1 || x.tone === "ok" || ENDS.has(x.id)).map((x) => x.id);
}
export const specFor = (id: string) => APP_SPECS.find((s) => s.id === id) ?? null;
/** The app ids on the shared record engine among an area's apps, and the stages that end each, for the home API. */
export function engineApps(d: Domain): { ids: string[]; done: string[] } {
  const ids = [...new Set(appsOf(d).map((a) => a.to.match(/^\/m\/([a-z0-9-]+)$/)?.[1]).filter((x): x is string => !!x && !!specFor(x)))];
  return { ids, done: ids.flatMap((id) => doneStages(specFor(id)!).map((st) => `${id}:${st}`)) };
}

// Live courageMFG screens: which record type's counts belong on the tile, and what to call them.
const STATS: Record<string, { type: string; show: "open" | "total"; word: string }> = {
  "/issues": { type: "issue", show: "open", word: "open" }, "/capas": { type: "capa", show: "open", word: "open" },
  "/accounts": { type: "account", show: "total", word: "accounts" }, "/opportunities": { type: "opportunity", show: "open", word: "open" },
  "/contracts": { type: "contract", show: "open", word: "active" }, "/plm/changes": { type: "eco", show: "open", word: "open" },
  "/item-setups": { type: "item_setup", show: "open", word: "in setup" }, "/maintenance": { type: "work_order", show: "open", word: "open" },
  "/maintenance/pm": { type: "pm", show: "open", word: "active" }, "/maintenance/assets": { type: "asset", show: "open", word: "assets" },
  "/service-desk": { type: "ticket", show: "open", word: "open" }, "/factory": { type: "factory_request", show: "open", word: "open" },
  "/factory/tasks": { type: "factory_task", show: "open", word: "open" }, "/marketing/leads": { type: "lead", show: "open", word: "open" },
  "/marketing/campaigns": { type: "campaign", show: "open", word: "running" }, "/marketing/emails": { type: "email", show: "total", word: "emails" },
  "/marketing/sequences": { type: "sequence", show: "open", word: "on" }, "/cpm": { type: "cpm_version", show: "total", word: "versions" },
};

// Apps whose records mostly sit in a live stage (an effective document, a gauge in service): count what needs work.
const WORK: Record<string, { stages: string[]; word: string }> = {
  "doc-control": { stages: ["draft", "review", "approved"], word: "in review" },
  calibration: { stages: ["due", "out_for_cal", "out_of_tolerance"], word: "due or out" },
  environmental: { stages: ["review_due", "expired"], word: "need review" },
  "skills-matrix": { stages: ["assigned", "training"], word: "in training" },
  kpis: { stages: ["watch", "off_target"], word: "off target" },
};

/** What's in an app, for its tile: "29 open", "6 assets"; nothing when there's nothing to count. */
export function appStat(to: string, home: AreaHome | null): { n: number; word: string } | null {
  if (!home) return null;
  const path = to.split("?")[0];
  const m = path.match(/^\/m\/([a-z0-9-]+)$/);
  if (m) {
    const spec = specFor(m[1]);
    if (!spec) return null;
    const rows = home.apps.filter((r) => r.app === m[1]);
    const total = rows.reduce((t, r) => t + r.count, 0);
    if (!total) return null;
    const w = WORK[m[1]];
    if (w) return { n: rows.filter((r) => w.stages.includes(r.stage)).reduce((t, r) => t + r.count, 0), word: w.word };
    const done = new Set(doneStages(spec));
    const open = rows.filter((r) => !done.has(r.stage)).reduce((t, r) => t + r.count, 0);
    return open ? { n: open, word: "open" } : { n: total, word: total === 1 ? "record" : "records" };
  }
  const s = STATS[path];
  const c = s && home.counts[s.type];
  if (!s || !c || !c.total) return null;
  return { n: s.show === "open" ? c.open : c.total, word: s.word };
}

/** The area's apps as tiles: each with what it's for and what's open in it. */
export function AppTiles({ d, home }: { d: Domain; home: AreaHome | null }) {
  const apps = appsOf(d);
  if (!apps.length) return null;
  return (
    <div className="site-apps">
      {apps.map((a) => {
        const st = appStat(a.to, home);
        return (
          <Link key={a.to} to={a.to} className="site-app">
            <span className="name">{a.name}</span>
            {st && <span className="stat"><b>{st.n.toLocaleString("en-US")}</b> {st.word}</span>}
            {a.note && <span className="note">{a.note}</span>}
          </Link>
        );
      })}
    </div>
  );
}
